import { fromBaseRecord } from './from-base-context';
import type { BaseEnvelope } from './base-patch';
import { encounterCreatureField } from '../../shared/array-types';
import { readInventoryField } from './inventory-raw-readers';
import { attachEntitySavedSections, prepareEntityComputed, mutableEntityComputed } from './entity-saved-sections';
import { yieldBetweenMemberCompiles } from './compile-task';
import { mutableSurface } from './mutable-surface';
import { sameJson } from './json-change';
import { possessorInputFingerprintSource } from './possessor-compile';
import { plStateOf } from '../pl/pl-state';
import { plContractKey } from '../pl/pl-claim';
import { readItemField } from './item-field-reader';
import { applyCombatEncounterHeader, readCombatEncounterHeader } from '../combat/encounter-header';

type Row = Record<string, unknown>;
interface Preparation { entity: string; id: string; rev: number; viewer: BaseEnvelope['viewer']; promise: Promise<unknown>; value?: unknown }
const preparations = new WeakMap<object, Preparation>();
let preparationCount = 0;
export const entitySurfacePreparationMetrics = () => ({ preparations: preparationCount });

function* inventorySurfaceNodes(root: unknown): Generator<object> {
    const stack = [root], visited = new WeakSet<object>();
    while (stack.length) {
        const value = stack.pop();
        if (!value || typeof value !== 'object' || visited.has(value)) continue;
        visited.add(value);
        yield value;
        for (const key of Object.keys(value))
            if (key !== 'catalog' && key !== '_entity_base' && key !== '_inventory_base') stack.push((value as Row)[key]);
    }
}
function stampInventoryRow(value: object, permissions: Row): void {
    if (fromBaseRecord(value) && fromBaseRecord(value.inventory)) {
        const id = String(readInventoryField(value, 'inventory_id') ?? '').toUpperCase();
        if (id) value.permissions = permissions[id] ?? {};
    }
}

/** Prepare one revision once; each host receives its own mutable leaves at paint. */
export function prepareEntitySurface(envelope: BaseEnvelope, computed: unknown): Promise<unknown> {
    if (!computed || typeof computed !== 'object') return Promise.resolve(computed);
    const prior = preparations.get(computed);
    if (prior && prior.entity === envelope.entity && prior.id === envelope.entity_id && prior.rev === envelope.base_rev && sameJson(prior.viewer, envelope.viewer)) return prior.promise;
    const promise = prepare(envelope, computed);
    const entry: Preparation = { entity: envelope.entity, id: envelope.entity_id, rev: envelope.base_rev, viewer: envelope.viewer, promise };
    preparations.set(computed, entry);
    void promise.then(value => { entry.value = value; }, () => { if (preparations.get(computed) === entry) preparations.delete(computed); });
    return promise;
}
export function prepareCachedEntitySurface(envelope: BaseEnvelope, computed: unknown): unknown {
    if (!computed || typeof computed !== 'object') return computed;
    const prior = preparations.get(computed);
    if (prior?.value && prior.entity === envelope.entity && prior.id === envelope.entity_id && prior.rev === envelope.base_rev && sameJson(prior.viewer, envelope.viewer)) return prior.value;
    preparationCount++;
    const value = mutableEntityComputed(computed);
    const permissions = fromBaseRecord(envelope.viewer?.inventory) ? envelope.viewer.inventory : {};
    for (const node of inventorySurfaceNodes(value)) stampInventoryRow(node, permissions);
    preparations.set(computed, { entity: envelope.entity, id: envelope.entity_id, rev: envelope.base_rev, viewer: envelope.viewer, value, promise: Promise.resolve(value) });
    return value;
}
async function prepare(envelope: BaseEnvelope, computed: unknown): Promise<unknown> {
    preparationCount++;
    const next = await prepareEntityComputed(computed);
    const permissions = fromBaseRecord(envelope.viewer?.inventory) ? envelope.viewer.inventory : {};
    let visited = 0;
    for (const value of inventorySurfaceNodes(next)) {
        stampInventoryRow(value, permissions);
        if (++visited % 256 === 0) await yieldBetweenMemberCompiles();
    }
    return next;
}

/** A prepared surface can predate a PL heal of the same member inputs. */
function carryHeldMemberPl(previous: Row | undefined, next: Row): void {
    if (!previous || previous === next || !encounterCreatureField(previous.encounter_creatures, 'encounter_creature_id')) return;
    const held = plStateOf(previous);
    if (held.value === null || held.pending || held.source !== 'browser-frozen' || held.contract !== plContractKey()) return;
    const incoming = plStateOf(next);
    if ((incoming.source === 'browser-frozen' && !incoming.pending && incoming.contract === held.contract) || !fromBaseRecord(next.computed)
        || possessorInputFingerprintSource(previous) !== possessorInputFingerprintSource(next)) return;
    next.computed.pl = held.value;
    for (const key of ['pl_meta', 'status_effect_permanent_pl']) {
        const value = readItemField(fromBaseRecord(previous.computed) ? previous.computed : null, key);
        if (value === null) delete next.computed[key]; else next.computed[key] = value;
    }
    delete next.computed.pl_pending;
}

/** Shared browser output stays detached from per-viewer grants and mutable page references. */
export function paintEntitySurface(host: Row, envelope: BaseEnvelope, computed: unknown, savedMembers: readonly Row[] = [], prepared = false,
    savedCombatHeader?: Readonly<Row>): void {
    if (!fromBaseRecord(computed)) throw new TypeError('unported:entity-output-shape');
    // Saved sections and aliases belong to this host, never the shared preparation.
    const surface = prepared ? computed : prepareCachedEntitySurface(envelope, computed);
    const next = attachEntitySavedSections(envelope.base, mutableSurface(surface), true) as Row;
    const viewer = envelope.viewer;
    const permissions = fromBaseRecord(viewer?.permissions) ? viewer.permissions : {};
    const held = host._entity_base;
    const combatHeader = savedCombatHeader ?? readCombatEncounterHeader(host);
    let retainedCombatMembership = false;
    const members = new Map((Array.isArray(host.encounter_creatures_arr) ? host.encounter_creatures_arr : [])
        .filter(fromBaseRecord).map(row => [String(encounterCreatureField(row.encounter_creatures as Row, 'encounter_creature_id') ?? '').toUpperCase(), row]));
    const revision = (row: Row): number => Number(encounterCreatureField(row.encounter_creatures, 'movement_rev') ?? 0);
    const saved = new Map<string, Row>();
    for (const row of savedMembers) {
        const id = String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase();
        if (id && (!saved.has(id) || revision(row) > revision(saved.get(id)!))) saved.set(id, row);
    }
    // Replace, rather than merge: removed statuses, zero vitals and departed members must leave the surface.
    for (const key of Object.keys(host)) if (!key.startsWith('_') && key !== 'preview_context') delete host[key];
    Object.assign(host, next, { permissions: mutableSurface(permissions), _entity_base: held ?? envelope, _entity_refusal: null });
    const memberViewers = fromBaseRecord(viewer?.members) ? viewer.members : {};
    const inventoryViewers = fromBaseRecord(viewer?.inventory) ? viewer.inventory : {};
    const memberRevisions = new Map((Array.isArray(envelope.base.encounter_creatures_arr) ? envelope.base.encounter_creatures_arr : [])
        .filter(fromBaseRecord).map(member => [String(member.entity_id).toUpperCase(), member.base_rev]));
    const stampInventory = (value: unknown): void => {
        for (const node of inventorySurfaceNodes(value)) stampInventoryRow(node, inventoryViewers);
    };
    const rows = Array.isArray(host.encounter_creatures_arr) ? host.encounter_creatures_arr : [];
    for (let index = 0; index < rows.length; index++) {
        let row = rows[index];
        if (!fromBaseRecord(row)) continue;
        let retained = false;
        const incomingCombatId = String(encounterCreatureField(row.encounter_creatures, 'combat_id') ?? '').toUpperCase();
        const id = encounterCreatureField(row.encounter_creatures as Row, 'encounter_creature_id');
        const canonical = members.get(String(id).toUpperCase());
        const newer = saved.get(String(id).toUpperCase());
        // A newly fetched host has no aliases yet. Carry the complete newer member
        // into its own tree, so discarding that detached host cannot retire live rows.
        if (newer && newer !== canonical && revision(newer) > revision(row)) { rows[index] = row = mutableSurface(newer); retained = true; }
        carryHeldMemberPl(canonical ?? newer, row);
        if (canonical) {
            // The enclosing revision does not order its members. A saved attack may
            // already have advanced this row while the enclosing compile was in flight.
            if (canonical !== row && revision(canonical) <= revision(row)) {
                for (const key of Object.keys(canonical)) if (!key.startsWith('_') && key !== 'preview_context') delete canonical[key];
                Object.assign(canonical, row, { _entity_refusal: row._entity_refusal ?? null, _inventory_refusal: row._inventory_refusal ?? null });
            } else if (revision(canonical) > revision(row)) retained = true;
            rows[index] = row = canonical;
        }
        retainedCombatMembership ||= retained
            && incomingCombatId !== String(encounterCreatureField(row.encounter_creatures, 'combat_id') ?? '').toUpperCase();
        if (!retained) for (const key of ['_entity_base', '_inventory_base', '_inventory_held']) {
            // The encounter just compiled this member and its inventory. Retire
            // covered shells; a newer standalone input still owes its own adoption.
            const held = row[key], revision = memberRevisions.get(String(id).toUpperCase());
            if (fromBaseRecord(held) && String(held.entity_id).toUpperCase() === String(id).toUpperCase()
                && typeof revision === 'number' && typeof held.base_rev === 'number' && held.base_rev <= revision) delete row[key];
        }
        row.permissions = mutableSurface(memberViewers[String(id).toUpperCase()] ?? {});
        if (retained) stampInventory(row);
    }
    // Repainting the shell also paints canonical hosts before encounter-update
    // runs. Retained newer membership must keep its header at this first write.
    if (retainedCombatMembership) applyCombatEncounterHeader(host, combatHeader);
}
